import { Component, OnDestroy, OnInit, ViewChild } from "@angular/core";
import { MatDrawer } from "@angular/material/sidenav";
import { Router } from "@angular/router";
import { Store } from "@ngrx/store";
import { Observable, Subscription } from "rxjs";
import { TokenRefresh } from "./shared/services/token-refresh.service";
import { HttpErrorResponse } from "@angular/common/http";
import { JwtHelperService } from "@auth0/angular-jwt";
import { environment } from "@environments";

import { selectToggleSidenav } from "./core/modules/users/state/selectors/user.selectors";
import { SidenavManagerService } from "./shared/services/sidenav.manager.service";
import { default as pack } from "../../package.json";
import { AnalyticsService } from "./shared/services/analytics.service";
import { Utils } from "./shared/utils/utils";
import { AuthService } from "./core/modules/auth/services/auth.service";
import { RenovateService } from "./shared/services/renovate.service";
import * as UserActions from './core/modules/users/state/actions/user.actions';
import { SedsModalService } from "@serasa/shared/seds";
import { RenovateModalExpiredComponent } from "./shared/components/renovate-modal-expired/renovate-modal-expired.component";
import { isMockEnvironment } from "./shared/utils/mock-session.util";

type EnvType = "stg" | "uat" | "prod";

@Component({
    selector: "app-root",
    templateUrl: "./app.component.html",
    styleUrls: ["./app.component.scss"],
    standalone: false
})
export class AppComponent implements OnInit, OnDestroy {

  @ViewChild("drawer", { static: true }) public drawer!: MatDrawer;
  public isOpen$?: Observable<boolean | undefined>;
  public packageVersion = "";
  public Utils = Utils;
  public renovateService?: RenovateService;

  private _subs: Subscription[] = [];

  constructor(
    private readonly _analyticsService: AnalyticsService,
    private readonly _authService: AuthService,
    private readonly _jwtHelper: JwtHelperService,
    private readonly _renovateService: RenovateService,
    private readonly _router: Router,
    private readonly _sidenavManagerService: SidenavManagerService,
    private readonly _tokenRefresh: TokenRefresh,
    private readonly store: Store,
    private readonly _modalService: SedsModalService
  ) {
    this.packageVersion = pack.version;
  }

  ngOnInit(): void {
    const isForbiddenRouter = Utils.locationHref().includes("forbidden");
    const isAuthenticated = this._authService.isAuthenticated();
    
    this._handleSetTimeout(isForbiddenRouter);
    this._handleObservables(isForbiddenRouter, isAuthenticated);
  }

  private _sidenavHandler(): void {
    this.isOpen$ = this.store.select(selectToggleSidenav);
    this._subs.push(
      this.isOpen$.subscribe((isOpen: any) => {
        if (!isOpen) {
          this.drawer.close();
          this._router.navigate([{ outlets: { sidenav: null } }]);
        } else {
          this.drawer.open();
        }
      }),
    );
  }

  private _setIntervalToRenovateToken(): void {
    this._subs.push(
      this._tokenRefresh
        .start("cookie", environment.env as EnvType, 15)
        .subscribe({
          next: (response: any) => {
            Utils.setCookie("accessToken", response.accessToken);
          },
          error: (_: HttpErrorResponse) => {
            this._router.navigate(["forbidden"]);
          },
        }),
    );
  }

  private _handleSetTimeout(isForbiddenRouter: boolean): void {
    if (isMockEnvironment()) {
      return;
    }

    setTimeout(() => {
      const accessToken = Utils.getCookie("accessToken");

      if (!isForbiddenRouter && !this._jwtHelper.isTokenExpired(accessToken)) {
        this._setIntervalToRenovateToken();
      }
    }, 2000);
  }

  private _handleObservables(isForbiddenRouter: boolean, isAuthenticated: boolean): void {
    this._subs.push(this._renovateService.renovateErrorSubject.subscribe((error: HttpErrorResponse | null) => {
      if (error) {
        this.store.dispatch(UserActions.loadAccountResponseFailure({ error }));
        this._openModalRenovateExpired();
      }
    }));

    this._subs.push(
      this._sidenavManagerService.navigate$.subscribe((path: string) => {
        if (path) {
          this._sidenavHandler();
          this._router.navigate(["", { outlets: { sidenav: path } }]);
        } else {
          if (!isForbiddenRouter && isAuthenticated) {
            Utils.dispatchUserList(this._analyticsService);
          }
        }
      }),
    );
  }

  private _openModalRenovateExpired(): void {
    const options = {
      width: '512px',
    };

    this._modalService.open(RenovateModalExpiredComponent, null, options);
  }

  ngOnDestroy(): void {
    for (const sub of this._subs) {
      sub.unsubscribe();
    }
  }
}
